import type { SVGProps } from 'react';

/**
 * Icon set: stroked SVG paths on a 24-grid, rendered at 16px.
 *
 * Deliberately hand-rolled rather than pulled from a library: a dozen glyphs is
 * not worth 60KB of dependency, and every icon here shares one stroke weight
 * and one grid, which is what makes a set look designed rather than collected.
 *
 * Unicode characters were used for this first — they inherit the text font, so
 * weight, alignment and size drift between glyphs and the result reads as
 * placeholder art no matter how the rest is styled.
 */

export type IconName =
  | 'dashboard' | 'activity' | 'accounts' | 'resellers' | 'users'
  | 'extension' | 'plan' | 'rates' | 'carrier' | 'hash' | 'billing'
  | 'invoice' | 'server' | 'settings' | 'phone' | 'queue' | 'ivr'
  | 'clock' | 'media' | 'chevronRight' | 'chevronDown' | 'chevronUp' | 'external'
  | 'plus' | 'search' | 'check' | 'close' | 'info' | 'warning' | 'trash'
  | 'edit' | 'panelLeft' | 'sun' | 'moon' | 'contrast' | 'logout' | 'arrowLeft'
  | 'callIn' | 'callOut' | 'callInternal' | 'play' | 'pause' | 'globe';

const PATHS: Record<IconName, string> = {
  dashboard:    'M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z',
  activity:     'M3 12h4l3 8 4-16 3 8h4',
  accounts:     'M3 21V7l6-4 6 4v14M9 21v-5h3v5M21 21V11l-6-4M6 10h.01M6 14h.01',
  resellers:    'M3 7h18M3 12h18M3 17h18M7 3v18',
  users:        'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  extension:    'M9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4M7 7h10v10H7z',
  plan:         'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  rates:        'M3 3v18h18M7 15l4-4 3 3 5-6',
  carrier:      'M4 7h10l-3-3M20 17H10l3 3M4 7l3 3M20 17l-3-3',
  hash:         'M4 9h16M4 15h16M10 3 8 21M16 3l-2 18',
  billing:      'M3 6h18v13H3zM3 10h18M7 15h4',
  invoice:      'M6 2h9l5 5v15H6zM15 2v5h5M9 13h6M9 17h4',
  server:       'M3 4h18v6H3zM3 14h18v6H3zM7 7h.01M7 17h.01',
  settings:     'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.3a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.7 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1',
  phone:        'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2',
  queue:        'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  ivr:          'M9 3H5a2 2 0 0 0-2 2v4M15 3h4a2 2 0 0 1 2 2v4M9 21H5a2 2 0 0 1-2-2v-4M15 21h4a2 2 0 0 0 2-2v-4M8 9h8M8 13h5',
  clock:        'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 6v6l4 2',
  media:        'M9 18V5l12-2v13M9 13l12-2M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  chevronRight: 'm9 18 6-6-6-6',
  chevronDown:  'm6 9 6 6 6-6',
  chevronUp:    'm18 15-6-6-6 6',
  external:     'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  plus:         'M12 5v14M5 12h14',
  search:       'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3',
  check:        'm20 6-11 11-5-5',
  close:        'M18 6 6 18M6 6l12 12',
  info:         'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 16v-4M12 8h.01',
  warning:      'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0',
  trash:        'M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6',
  edit:         'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z',
  panelLeft:    'M3 3h18v18H3zM9 3v18',
  sun:          'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon:         'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8',
  contrast:     'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 2v20',
  logout:       'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  arrowLeft:    'M19 12H5M12 19l-7-7 7-7',
  /**
   * Direction, as a glyph rather than a word.
   *
   * A call list is read by shape: three words of the same length in the same
   * colour force you to read every row. The arrows point the way the call went --
   * in towards us, out away from us, and both ways for a call that never left the
   * account -- and each is paired with a text label for a screen reader, because
   * an arrow alone is not a direction to somebody who cannot see it.
   */
  callIn:       'M18 6 8 16M14 16H8v-6',
  callOut:      'M6 18 16 8M10 8h6v6',
  callInternal: 'M7 8h11M7 8l3-3M7 8l3 3M17 16H6M17 16l-3-3M17 16l-3 3',
  play:         'M8 5v14l11-7z',
  pause:        'M9 5v14M15 5v14',
  globe:        'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, ...rest }: IconProps) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={1.6}
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
      style={{ flex: '0 0 auto', display: 'block' }}
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/**
 * The brand mark. Flat, not a gradient: a gradient logo dates a product
 * instantly and competes with the interface for attention.
 */
export function BrandMark({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true"
      style={{ flex: '0 0 auto', display: 'block' }}>
      <rect width="32" height="32" rx="7" fill="var(--arc-brand)" />
      <path
        d="M9 22.5 16 9l7 13.5" stroke="#fff" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round" fill="none"
      />
      <path d="M12.2 18.4h7.6" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
